import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { FeeRunScheduleView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface FeeRunAutomationProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

/**
 * Finance's switch for the automatic monthly fee run. It is off until finance turns it on, and turning
 * it on asks for a confirmation first because the worker may post the current month within minutes.
 */
export function FeeRunAutomation({
  locale,
  organizationId,
  csrfToken,
}: FeeRunAutomationProps): React.JSX.Element {
  const path = `/api/v1/organizations/${organizationId}/billing/fee-run-schedule`;
  const [schedule, setSchedule] = useState<FeeRunScheduleView | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  const load = useCallback(async () => {
    try {
      setSchedule(await apiRequest<FeeRunScheduleView>(path));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  async function change(automatic: boolean): Promise<void> {
    setErrorKey(null);
    try {
      setSchedule(
        await apiRequest<FeeRunScheduleView>(path, {
          method: 'PUT',
          csrfToken,
          body: { automatic },
        }),
      );
      setConfirming(false);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  const lastRun = schedule?.lastRun ?? null;

  return (
    <Box component="section" aria-labelledby="fee-run-auto-title" sx={{ mb: 3 }}>
      <Typography variant="subtitle1" component="h3" id="fee-run-auto-title" gutterBottom>
        {translate(locale, 'billing.fees.auto.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {translate(locale, 'billing.fees.auto.description')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {schedule !== null && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
          <Chip
            color={schedule.automatic ? 'success' : 'default'}
            label={translate(
              locale,
              schedule.automatic ? 'billing.fees.auto.on' : 'billing.fees.auto.off',
            )}
          />
          {schedule.automatic ? (
            <Button
              variant="outlined"
              onClick={() => {
                void change(false);
              }}
            >
              {translate(locale, 'billing.fees.auto.disable')}
            </Button>
          ) : (
            !confirming && (
              <Button
                variant="outlined"
                onClick={() => {
                  setConfirming(true);
                }}
              >
                {translate(locale, 'billing.fees.auto.enable')}
              </Button>
            )
          )}
        </Stack>
      )}
      {confirming && schedule?.automatic === false && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void change(true);
                }}
              >
                {translate(locale, 'billing.fees.auto.confirmEnable')}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                {translate(locale, 'billing.fees.auto.cancel')}
              </Button>
            </Stack>
          }
        >
          {translate(locale, 'billing.fees.auto.confirm')}
        </Alert>
      )}
      {schedule !== null && (
        <Box sx={{ mt: 1 }}>
          {lastRun === null ? (
            <Typography variant="body2">{translate(locale, 'billing.fees.auto.noRun')}</Typography>
          ) : (
            <Alert severity={lastRun.status === 'failed' ? 'error' : 'info'}>
              {translate(locale, 'billing.fees.auto.lastRun')}: {lastRun.localDay} ·{' '}
              {lastRun.billingPeriod} ·{' '}
              {translate(
                locale,
                lastRun.status === 'failed'
                  ? 'billing.fees.auto.failed'
                  : 'billing.fees.auto.succeeded',
              )}
              {lastRun.status === 'succeeded' && lastRun.createdCount !== null && (
                <>
                  {' '}
                  · {translate(locale, 'billing.fees.run.result.created')}:{' '}
                  {String(lastRun.createdCount)} ·{' '}
                  {translate(locale, 'billing.fees.run.result.review')}:{' '}
                  {String(lastRun.awaitingReviewCount ?? 0)}
                </>
              )}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
